import type { AppConfig } from '@/types/config'

export const readingPlusConfig: AppConfig = {
  api: {
    baseUrl: import.meta.env.VITE_API_URL,
    appUrl: import.meta.env.VITE_APP_URL,
    version: 'v1'
  },
  app: {
    id: 'reading-plus',
    name: 'Bookshelf',
    description: 'Discover and read exclusive content from independent authors',
    contentId: 4,
    subscriptionPlanId: 1,
    logo: {
      default: '/assets/images/reading-plus/logo.svg',
      icon: '/assets/images/reading-plus/icon.svg'
    }
  },

  theme: {
    colors: {
      primary: '#41d19a', // Forest green (44, 85, 48)
      secondary: '#4A5568', // Gray (74, 85, 104)
      accent: '#68D391', // Light green (104, 211, 145)
      background: '#F7FAFC' // Light gray (247, 250, 252)
    },
    dark: {
      primary: '#41d19a', // Green
      secondary: '#AAAEB4', // Same forest green
      accent: '#41d19a', // Same light green
      background: '#1A202C' // Dark gray (26, 32, 44)
    },
    fonts: {
      heading: 'Inter',
      body: 'Inter'
    },
    form: {
      padding: {
        container: 'py-8 px-4 sm:px-10',
        field: 'space-y-4'
      },
      rounding: {
        button: 'rounded-xl',
        input: 'rounded-xl'
      }
    }
  },

  layout: {
    maxWidth: '2xl',
    headerHeight: '16',
    playerHeight: '72px',
    screens: {
      home: [
/*         {
          "type": "slider",
          "layout": "slider",
          "id": "1",
          "data": {
            "title": "",
            "list": [],
            "params": {
              "config": {
                "lazyload": false
              }
            }
          }
        }, */
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "Featured Books",
            "list": [],
            "params": {
              "filter[category]": "featured"
            }
          },
          "title": "Featured Books",
          "limit": 20
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "New Releases",
            "list": [],
            "params": {
              "filter[category]": "new"
            }
          },
          "title": "New Releases"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "Included in Subscription",
            "list": [],
            "params": {
              "filter[is_subscription]": 1
            }
          },
          "title": "Included in Subscription"
        },
        {
          "type": "categories",
          "layout": "masonry",
          "data": {
            "title": "Browse by Genre",
            "list": [],
            "params": {
              "limit": 8
            }
          },
          "title": "Browse by Genre",
          "limit": 8
        },
      ],
      search: [
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "1",
          "data": {
            "title": "",
            "list": [],
            "params": {
              "limit": 8,
              "config": {
                "lazyload": false
              }
            }
          }
        },
        {
          "type": "categories",
          "layout": "two_column",
          "data": {
            "title": "Discover by Genre",
            "list": [],
            "params": {
              "limit": 8
            }
          },
          "title": "Discover by Genre",
          "limit": 8
        },

        {
          "type": "titles",
          "layout": "horizontal",
          "data": {
            "title": "Featured Books",
            "list": [],
            "params": {
              "filter[category]": "featured"
            }
          },
          "title": "Featured Books",
          "limit": 20
        },
        {
          "type": "authors",
          "layout": "two_column",
          "data": {
            "title": "Popular Authors",
            "list": [],
            "params": {
              "limit": 8
            }
          },
          "title": "Popular Authors",
          "limit": 8
        },
      ],
      library: [
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "purchased",
          "data": {
            "title": "My Library",
            "list": [],
            "params": {
              "filter[library]": "purchased"
            }
          },
          "title": "My Library"
        },
        {
          "type": "titles",
          "layout": "horizontal",
          "id": "bookmarked",
          "data": {
            "title": "Bookmarked",
            "list": [],
            "params": {
              "filter[library]": "bookmarked"
            }
          },
          "title": "Bookmarked"
        },
      ]
    }
  },

  features: {
    shop: true, // enable use of shop and purchase feature
    ads: true, // enable use of ads
    guestMode: true, // enable use of guest mode
    favorites: true, // enable use of favorites, user can add books to favorites
    darkMode: true, // enable use of dark mode
    search: true, // enable use of search
    player: false, // enable use of player
    sharing: true, // enable use of sharing
    epubReader: true, // enable use of epub reader
    video: false, // enable use of video player
    hasPreview: false, // enable use of preview on product detail page
    customAnimation: true // enable use of custom animation on login/register pages
  },

  defaultMode: 'light' as const,

  subscription: {
    stripePlanId: 'price_1NZyVwGw3TmAggLVxS0lKNhf',
    price: '4.99',
    currency: 'GBP',
    features: [
      'High Quality Content',
      'Offline Access', 
      'Ad Free Experience',
      'No Commitment',
      'Cancel Anytime'
    ],
    usps: [
      {
        title: 'Premium Reading Experience',
        description: 'Enjoy all books with high-quality formatting and typography for the best reading experience.',
        icon: 'book'
      },
      {
        title: 'Offline Reading',
        description: 'Download your favorite books and read them even without internet connection.',
        icon: 'download'
      },
      {
        title: 'Ad-Free Experience',
        description: 'Enjoy uninterrupted reading with no ads or promotions.',
        icon: 'like'
      },
      {
        title: 'Exclusive Content',
        description: 'Get access to subscriber-only books, early releases, and original publications.',
        icon: 'subscribe'
      }
    ]
  },

  urls: {
    terms: 'https://reading.plus/terms',
    privacy: 'https://reading.plus/privacy',
    support: 'https://reading.plus/support'
  },

  seo: {
    title: 'Reading Plus - Your Personal Reading Library',
    description: 'Discover and read your favorite books',
    ogImage: '/assets/images/reading-plus/og-image.svg',
    defaults: {
      titles: {
        title: 'Books | Reading Plus',
        description: 'Explore our collection of premium books',
        type: 'article'
      },
      categories: {
        title: 'Categories | Reading Plus',
        description: 'Browse books by genre and category',
        type: 'website'
      },
      authors: {
        title: 'Authors | Reading Plus',
        description: 'Discover talented authors and their works',
        type: 'website'
      },
      series: {
        title: 'Series | Reading Plus',
        description: 'Follow your favorite book series',
        type: 'website'
      },
      casts: {
        title: 'Narrators | Reading Plus',
        description: 'Meet the talented narrators behind our audiobooks',
        type: 'website'
      }
    }
  }
}
